import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import Header from "./Header";
import DashboardFooter from "./DashboardFooter";
import { FaEdit, FaTrash, FaEye, FaDownload, FaPlus, FaSync, FaSearch, FaTimes } from "react-icons/fa";
// ----- ESLint-friendly confirm -----
/**
 * Confirm deletion with user
 * @param {string} message - Confirmation message
 * @returns {boolean} - true if confirmed
 */
const confirmDelete = (message) => {
  // eslint-disable-next-line no-alert
  return window.confirm(message);
};

const API_URL = "https://auxititpt.org/Backend/letterhead_api.php";
const LETTERHEAD_IMAGE = "https://auxititpt.org/public/uploads/Letter_Head_Axilium_English.jpg";

const LetterheadDocuments = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [formData, setFormData] = useState({ title: "", content: "" });

  useEffect(() => {
    fetchDocs();
  }, []);

const fetchDocs = async () => {
  setLoading(true);
  try {
    const res = await fetch(API_URL);
    const text = await res.text(); // Read as text first

    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error("Invalid JSON:", text);
      alert("Server returned invalid response. Check console.");
      return;
    }

    if (data.status === "error") {
      alert("Error: " + data.message);
      setDocs([]);
    } else {
      setDocs(Array.isArray(data) ? data : []);
    }
  } catch (err) {
    console.error(err);
    alert("Network error. Check if backend is running.");
  } finally {
    setLoading(false);
  }
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      alert("Title and content are required");
      return;
    }

    const url = editDoc ? `${API_URL}?id=${editDoc.id}` : API_URL;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      const result = await res.json();
      if (result.status === "success") {
        alert(editDoc ? "Updated!" : "Saved!");
        fetchDocs();
        resetForm();
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const resetForm = () => {
    setFormData({ title: "", content: "" });
    setEditDoc(null);
    setShowForm(false);
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (doc) => {
    setFormData({ title: doc.title, content: doc.content });
    setEditDoc(doc);
    setShowForm(true);
  };

const handleDelete = async (id) => {
  if (!confirmDelete("Delete this document permanently?")) return;   // <-- changed

  try {
    const res = await fetch(`${API_URL}?id=${id}`, { method: "DELETE" });
    const result = await res.json();
    if (result.status === "success") {
      alert("Deleted!");
      fetchDocs();
    }
  } catch (err) {
    alert("Delete failed");
  }
};

  const handleView = (doc) => {
    setSelectedDoc(doc);
    setShowViewModal(true);
  };

  // ————————————————————————————————————————
  // NEW: Print with Letterhead (Same as Staff)
  // ————————————————————————————————————————
  const fixUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("https")) return url;
    const clean = url.startsWith("/") ? url : "/" + url;
    return `https://localhost${clean}`;
  };

     const handlePrintLetterhead = (doc) => {
    const popup = window.open("", "_blank", "width=900,height=1000,scrollbars=no,resizable=yes");

    // Escape HTML to prevent XSS
    const escapeHtml = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };

    const safeTitle = escapeHtml(doc.title);
    const safeContent = escapeHtml(doc.content).replace(/\n/g, '<br>');

    // Current date: 15-Nov-2025
    const printDate = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).replace(/ /g, "-");

    popup.document.write(`
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${safeTitle}</title>
  <style>
    @page { size: A4; margin: 0; }
    html, body { margin:0; padding:0; height:297mm; overflow:hidden; }
    body {
      font-family: 'Georgia', serif;
      position: relative;
      font-size: 0.95rem;
      line-height: 1.5;
      color: #000;
    }

    /* Letterhead Background */
    body::before {
      content: '';
      position: fixed;
      top: 0; left: 0;
      width: 210mm; height: 297mm;
      background: url('${fixUrl(LETTERHEAD_IMAGE)}') no-repeat top center / 100% auto;
      z-index: -1;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    /* Content starts after letterhead */
    .content {
      padding: 105mm 15mm 20mm 15mm;
      position: relative;
      z-index: 1;
    }

    .doc-title {
      text-align: center;
      font-size: 1.4rem;
      font-weight: bold;
      text-decoration: underline;
      margin-bottom: 20px;
      color: #003366;
    }

    .doc-body {
      font-size: 1rem;
      line-height: 1.6;
      text-align: justify;
    }

    .signature {
      margin-top: 40px;
      text-align: right;
      font-weight: bold;
      font-size: 1rem;
    }

    /* NEW: Styled Date – Elegant & Formal */
    .print-date {
      position: absolute;
      top: 85mm;
      right: 18mm;
      font-family: 'Times New Roman', Times, serif;
      font-size: 1.05rem;
      font-weight: 600;
      font-style: italic;
      color: #1a1a1a;
      letter-spacing: 0.5px;
      z-index: 2;
      text-shadow: 0 0 1px rgba(0,0,0,0.1);
    }

    @media print {
      body::before, .print-date {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
    }
  </style>
</head>
<body>
  <div class="content">
    <div class="print-date">Date: ${printDate}</div>

    <div class="doc-title">${safeTitle}</div>
    <div class="doc-body">${safeContent}</div>
    <div class="signature">Principal Signature</div>
  </div>

  <script>
    window.onload = () => setTimeout(() => window.print(), 500);
    window.onafterprint = () => window.close();
    const mq = window.matchMedia('print');
    mq.addEventListener('change', (m) => { if (!m.matches) setTimeout(() => window.close(), 100); });
  </script>
</body>
</html>
    `);

    popup.document.close();
    popup.focus();
  };

  const normalizedQuery = searchQuery.toLowerCase().trim();
  const filteredDocs = docs.filter(d =>
    d.title.toLowerCase().includes(normalizedQuery) ||
    d.content.toLowerCase().includes(normalizedQuery)
  );

  if (loading) {
    return (
      <div className="d-flex dashboard-container">
        <Sidebar />
        <div className="main-content flex-grow-1">
          <Header />
          <div className="container py-5 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
          <DashboardFooter />
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex dashboard-container">
      <Sidebar />
      <div className="main-content flex-grow-1">
        <Header />
        <div className="container py-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="fw-bold">Letterhead Documents</h3>
            <div>
              <button className="btn btn-outline-secondary me-2" onClick={fetchDocs}>
                <FaSync className="me-1" /> Refresh
              </button>
              <button className="btn btn-primary" onClick={openAddForm}>
                <FaPlus className="me-2" /> New Document
              </button>
            </div>
          </div>

          <div className="card mb-4 p-3">
            <div className="input-group">
              <span className="input-group-text"><FaSearch /></span>
              <input
                type="text"
                className="form-control"
                placeholder="Search title or content..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="btn btn-outline-secondary" onClick={() => setSearchQuery("")}>
                  <FaTimes />
                </button>
              )}
            </div>
          </div>

          {showForm && (
            <form className="card shadow-lg p-4 mb-5" onSubmit={handleSubmit}>
              <h5 className="fw-bold mb-3 text-primary">
                {editDoc ? "Edit Document" : "New Document"}
              </h5>
              <div className="mb-3">
                <label className="form-label">Title *</label>
                <input
                  type="text"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Content *</label>
                <textarea
                  name="content"
                  className="form-control"
                  rows={12}
                  value={formData.content}
                  onChange={handleChange}
                  required
                  style={{ fontFamily: "Georgia, serif" }}
                />
              </div>
              <div className="text-center">
                <button type="submit" className="btn btn-success px-4">
                  {editDoc ? "Update" : "Save"}
                </button>
                <button type="button" className="btn btn-secondary ms-2" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.map((doc, i) => (
                  <tr key={doc.id}>
                    <td>{i + 1}</td>
                    <td>{doc.title}</td>
                    <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                    <td className="text-center">
                      <button className="btn btn-sm btn-info me-1" onClick={() => handleView(doc)} title="View">
                        <FaEye />
                      </button>
                      <button className="btn btn-sm btn-warning me-1" onClick={() => handleEdit(doc)} title="Edit">
                        <FaEdit />
                      </button>
                      <button className="btn btn-sm btn-danger me-1" onClick={() => handleDelete(doc.id)} title="Delete">
                        <FaTrash />
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handlePrintLetterhead(doc)}
                        title="Print with Letterhead"
                      >
                        <FaDownload />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredDocs.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted">
                      No documents found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* View Modal */}
          {showViewModal && selectedDoc && (
            <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => setShowViewModal(false)}>
              <div className="modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{selectedDoc.title}</h5>
                    <button className="btn-close" onClick={() => setShowViewModal(false)}></button>
                  </div>
                  <div className="modal-body">
                    <pre style={{ whiteSpace: "pre-wrap", fontFamily: "Georgia, serif" }}>
                      {selectedDoc.content}
                    </pre>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <DashboardFooter />
      </div>
    </div>
  );
};

export default LetterheadDocuments;